import { deserialize, deserializeArray } from 'santee-dcts';
import { map, refCount, switchAll, flatMap, publishReplay } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { HttpClient} from '@angular/common/http';
import { Injectable, InjectionToken } from '@angular/core';

import { Settings } from '../../core/index';
import { Email, EmailPreferenceReadModel, EmailPreference } from './email';
import { EmailPreferenceLevelType } from './levels/email-preference-level-type';
import { User } from '../user';
import { ActivityLogger } from '../../activities/activity-logger';

export const EMAIL_PREFERENCE_STORAGE_TOKEN = new InjectionToken('EMAIL_PREFERENCE_STORAGE');

export interface LoadEmailPreferencesParams {
    user: User;
    levelIndicator: EmailPreferenceLevelType;
}

@Injectable()
export class EmailPreferencesStorage {
    public get emailPreferences(): Observable<EmailPreferenceReadModel[]> {
        return this.emailPreferencesInternal;
    }

    private readonly emailPreferencesInternal: Observable<EmailPreferenceReadModel[]>;
    private readonly refresher: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private readonly paramsSubject: BehaviorSubject<LoadEmailPreferencesParams> = new BehaviorSubject<LoadEmailPreferencesParams>(null);

    constructor(
        private readonly http: HttpClient,
        private readonly settings: Settings,
        private readonly activityLogger: ActivityLogger,
    ) {
        const sources = [this.refresher, this.paramsSubject];
        this.emailPreferencesInternal = combineLatest(sources)
            .pipe(
                map(([needToShowSpinner, params] : [boolean, LoadEmailPreferencesParams]) => {
                    if (needToShowSpinner) {
                        return this.returnNullToShowSpinner();
                    }

                    return this.http
                        .get<Object[]>(`${this.settings.getSpecificUserApiUrl(params.user)}/email-preferences/level/${params.levelIndicator}/user/${params.user.metadata.userId}`)
                        .pipe(map((response: EmailPreferenceReadModel[]) => deserializeArray(response, EmailPreferenceReadModel)));
                }),
                flatMap(x => of(of(null), x)),
                switchAll(),
                publishReplay(1),
                refCount(),
            );
    }

    public reloadEmailPreferences(params: LoadEmailPreferencesParams): void {
        this.paramsSubject.next(params);
    }

    public async saveEmailPreferences(emailPreferences: EmailPreference[], userPk: number): Promise<void> {
        this.activityLogger.logChangeEmailPreferencesActivity(emailPreferences, userPk);

        await this.http.put(`${this.settings.apiUrl}/email-preferences/user/${userPk}`, emailPreferences)
            .toPromise();
    }

    public async resetToDefault(emailPreferences: EmailPreference[], userPk: number): Promise<void> {
        this.activityLogger.logResetEmailPreferences(userPk);

        this.refresher.next(true);
        await this.http.put(`${this.settings.apiUrl}/email-preferences/user/${userPk}/reset`, emailPreferences)
            .toPromise();
        this.refresher.next(false);
    }

    public async saveOrUpdateEmail(email: Email, userPk: number): Promise<void> {
        this.refresher.next(true);
        if (email.sitelinkUserEmailId) {
            this.activityLogger.logEditEmailNotificationActivity(userPk);

            await this.http
                .put(`${this.settings.apiUrl}/emails/${email.sitelinkUserEmailId}?editedUserPk=${userPk}`, email)
                .pipe(map((response) => deserialize(response, Email)))
                .toPromise();
        } else {
            this.activityLogger.logAddEmailNotificationActivity(userPk);

            await this.http
                .post(`${this.settings.apiUrl}/emails?editedUserPk=${userPk}`, email)
                .toPromise();
        }
        this.refresher.next(false);
    }

    public async deleteEmails(emails: Email[], userPk: number): Promise<void> {
        this.activityLogger.logDeleteEmailNotificationActivity(userPk);

        this.refresher.next(true);
        await this.http
            .post(`${this.settings.apiUrl}/emails/delete?editedUserPk=${userPk}`, emails)
            .toPromise();
        this.refresher.next(false);
    }

    private returnNullToShowSpinner() {
        return of(null);
    }
}
